// @flow
import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { Link } from 'react-router'
import Helmet from 'react-helmet'
import media from '../../styles/media'
import { openContactModal } from '../../actions/modals'
import Button from '../../components/Button'
import BenefitIcons from '../../components/BenefitIcons'
import MarketList from '../../components/MarketList'
import debounce from 'lodash/debounce'

const Hero = styled.div`
  padding: 100px 0;
  background: url('/static/images/home-main.jpg');
  background-size: cover;
  background-position: center;
  position: relative;

  ${media.mdUp} {
    padding: 250px 0;
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    content: '';
    background: rgba(0, 0, 0, 0.3);
  }
`

const HeroContent = styled.div`
  color: #fff;
  position: relative;
  z-index: 1;
  text-align: center;
`

const HeroHeader = styled.h1`
  ${media.mdUp} {
    font-size: 3.5rem;
  }
`

const SearchContainer = styled.div`
  width: 100%;
  margin: 2rem auto;
  text-align: left;

  ${media.mdUp} {
    width: 80%;
  }

  ${media.lgUp} {
    width: 60%;
  }
`

const BlueSection = styled.section`
  padding: 3rem 0;
  color: #fff;
  background: linear-gradient(to right, #2d4473, #4c70ba);
`

const Blockquote = styled.blockquote`
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  border-left: 0.25rem solid #e1e6eb;
`

const OutlineButton = styled(Link)`
  border: 2px solid rgba(255, 255, 255, 0.7);
  color: rgba(255, 255, 255, 0.7);
  background: transparent;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;

  &:hover,
  &:focus {
    text-decoration: none;
    color: #fff;
    border-color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`

const ContactButton = styled.button`
  border: 2px solid rgba(255, 255, 255, 0.7);
  color: rgba(255, 255, 255, 0.7);
  background: transparent;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: none;
    color: #fff;
    border-color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`

const HeaderContainer = styled.div`
  position: relative;
  margin-bottom: 0.5rem;

  &::after {
    position: absolute;
    top: 50%;
    right: 0;
    left: 0;
    content: '';
    border-bottom: 2px solid #e1e6eb;
  }
`

const Header = styled.h2`
  text-transform: uppercase;
  background: rgb(240, 244, 247);
  padding-right: 2rem;
  z-index: 1;
  display: inline-block;
  margin-bottom: 0;
  position: relative;
`

const Lead = styled.p`
  font-size: 1.25rem;
  font-weight: 300;
`

const ImageSectionContainer = styled.section`
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;

  &::after {
    content: '';
    display: table;
    clear: both;
  }

  ${media.smUp} {
    width: 540px;
  }

  ${media.mdUp} {
    width: 720px;
  }

  ${media.lgUp} {
    width: 960px;
  }

  ${media.xlUp} {
    width: 1140px;
  }
`

const ImageSectionContent = styled.section`
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  padding: 4rem 2rem;

  ${media.smUp} {
    padding: 8rem 2rem;
  }
`

const SearchInput = styled.input`
  padding: 1rem;
  height: 40px;
  width: 100%;
  background: rgba(255, 255, 255, 0.8);
  border-color: transparent;
  border-radius: 4px;
`

const SearchResultsWrapper = styled.div`
  position: relative;
`

const SearchResultList = styled.ul`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  padding: 0;
  margin: 0;
  background: white;
  border-radius: 4px;
  list-style-type: none;
`

const SearchResultItem = styled.li`
  color: rgb(120, 125, 130);
  padding: 4px 8px;

  &:hover,
  &:focus {
    background: rgb(220, 225, 230);
    color: #014c8c;
    cursor: pointer;
  }
`

type Props = {
  router: Object,
  openContactModal: Function
}

class Home extends Component {
  constructor(props) {
    super(props)
    this.state = {
      searchResults: []
    }
    this.debouncedHandleSearchInput = debounce(this.handleSearchInput, 300)
  }

  props: Props
  searchInput: HTMLInputElement

  handleSearchInput = (text) => {
    if (!text) {
      this.setState({ searchResults: [] })
      return
    }

    const service = new window.google.maps.places.AutocompleteService()

    service.getQueryPredictions({ input: text }, (predictions, status) => {
      if (
        status != window.google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        this.setState({ searchResults: [] })
        return
      }

      this.setState({ searchResults: predictions })
    })
  }

  handleSearchResultSelect = (prediction) => {
    const city = prediction.structured_formatting.main_text
    this.props.router.push({
      pathname: '/properties',
      query: { q: city }
    })
  }

  render() {
    return (
      <div>
        <Helmet title="Central Illinois Real Estate" />
        <Hero>
          <HeroContent>
            <div className="container">
              <HeroHeader>We Believe in Smarter Home Buying</HeroHeader>
              <SearchContainer>
                <SearchResultsWrapper>
                  <SearchInput
                    placeholder="Where do you want to live?"
                    onKeyUp={(event) => {
                      const value = event.target.value
                      this.debouncedHandleSearchInput(value)
                    }}
                  />
                  <SearchResultList>
                    {this.state.searchResults.map((prediction) => (
                      <SearchResultItem
                        key={prediction.place_id}
                        onClick={() =>
                          this.handleSearchResultSelect(prediction)
                        }
                      >
                        {prediction.description}
                      </SearchResultItem>
                    ))}
                  </SearchResultList>
                </SearchResultsWrapper>
              </SearchContainer>
              <div style={{ fontSize: '1.5rem' }}>
                Our technology, analytics and guidance will help you get there.
              </div>
            </div>
          </HeroContent>
        </Hero>
        <BlueSection>
          <div className="container">
            <h1 style={{ marginBottom: '1rem' }}>
              <span style={{ marginRight: '10px' }}>Kief Realty</span>
              <em style={{ color: 'rgba(255,255,255,.7)', fontSize: '75%' }}>
                Modern, Transparent, Technology Focused
              </em>
            </h1>
            <Blockquote>
              Our full service team and online home buying tools make sure all
              the resources are at your fingertips to efficiently and
              effectively buy your next home. We provide a fast and smooth home
              buying experience, while saving you valuable time and money on
              your investment.
            </Blockquote>
            <div style={{ textAlign: 'center' }}>
              <OutlineButton to="/buying-with-us">Our story</OutlineButton>
            </div>
          </div>
        </BlueSection>
        <div style={{ padding: '4rem 0' }}>
          <div className="container">
            <HeaderContainer>
              <Header>Why Kief Realty?</Header>
            </HeaderContainer>
            <Lead style={{ marginBottom: '2rem' }}>
              We believe that the home buying process should be innovative,
              convenient, and without all the stress.
            </Lead>
            <BenefitIcons />
            <div style={{ margin: '2rem 0', textAlign: 'center' }}>
              <Button color="primary" to="/buying-with-us">
                Read full details
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}
            >
              <strong style={{ paddingRight: '1rem', marginBottom: '1rem' }}>
                Learn More
              </strong>
              <Link
                style={{ padding: '0 1rem', marginBottom: '1rem' }}
                to="/buying-with-us"
              >
                Buying with us
              </Link>
              <Link
                style={{ padding: '0 1rem', marginBottom: '1rem' }}
                to="/kief-realty-difference"
              >
                How we&apos;re different
              </Link>
              <Link
                style={{ padding: '0 1rem', marginBottom: '1rem' }}
                to="/rebate"
              >
                Our rebate
              </Link>
              <Link
                style={{ padding: '0 1rem', marginBottom: '1rem' }}
                to="/faq"
              >
                Common questions
              </Link>
            </div>
          </div>
        </div>
        <BlueSection style={{ textAlign: 'center' }}>
          <div className="container">
            <h2 style={{ marginBottom: '2rem' }}>
              Start the search for the home you want today
            </h2>
            <OutlineButton to="/properties">Browse online</OutlineButton>
          </div>
        </BlueSection>
        <div
          style={{
            background: 'url("/static/images/home-buyers.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: '80% center'
          }}
        >
          <ImageSectionContainer>
            <div
              className="col-xl-5 col-lg-6 col-md-8 col-xs-12"
              style={{ padding: 0 }}
            >
              <ImageSectionContent>
                <h2>
                  Buying a home can be a stressful process. Don’t worry,
                  we&apos;ll take care of everything.
                </h2>
                <Lead>
                  Kief Realty provides full service with 24/7 access to our
                  technology platform. You are in control and in the know about
                  your transaction every step of the way.
                </Lead>
                <ContactButton onClick={this.props.openContactModal}>
                  Contact us
                </ContactButton>
              </ImageSectionContent>
            </div>
          </ImageSectionContainer>
        </div>
        <div style={{ padding: '4rem 0' }}>
          <div className="container">
            <HeaderContainer>
              <Header>Uh... Say What Now?</Header>
            </HeaderContainer>
            <Lead>
              You heard us correctly. We charge you nothing to work with us, and
              actually pay you. On top of the added value and benefits
              you&apos;ve already received when allowing us to represent you in
              your home purchase, we&apos;ll pay you a rebate of up to $250.
              Just remember to spend it wisely. Or not, it&apos;s your money!
            </Lead>
            <img
              src="/static/images/rebate-monitor.png"
              alt="Rebate"
              style={{
                display: 'block',
                margin: '1rem auto',
                width: '500px',
                maxWidth: '100%'
              }}
            />
            <div style={{ textAlign: 'center' }}>
              <Button to="/rebate" color="primary">
                See search results
              </Button>
            </div>
          </div>
        </div>
        <div
          style={{
            background: 'url("/static/images/other-website.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <ImageSectionContainer>
            <div
              className="col-xl-5 col-lg-6 col-md-8 col-xs-12"
              style={{ padding: 0 }}
            >
              <ImageSectionContent>
                <h2>Found a home you like on another website?</h2>
                <Lead>
                  You can still work with us. And it costs you nothing!
                </Lead>
                <Lead>
                  Kief Realty will help you negotiate down to the best possible
                  value, guide you through every step of the process and handle
                  all the paperwork.
                </Lead>
                <Lead>
                  You found your home. Let us save you time and money, and
                  handle the rest for you.
                </Lead>
                <OutlineButton to="/buy-any-home">Buy any home</OutlineButton>
              </ImageSectionContent>
            </div>
          </ImageSectionContainer>
        </div>
        <BlueSection style={{ textAlign: 'center' }}>
          <div className="container">
            <h2>Kief Realty isn’t your average real estate team.</h2>
            <span className="fa fa-star" />
            <span className="fa fa-star" />
            <span className="fa fa-star" />
            <span className="fa fa-star" />
            <span className="fa fa-star" />
            <Lead>
              Great service is our aim. Your satisfaction is our success. An
              impressed customer is the best promoter we could ever hope for.
              Built on a foundation of integrity and transparency, our goal is
              to help you reach your goals, while putting your interests first
              in every situation.
            </Lead>
          </div>
        </BlueSection>
        <div style={{ padding: '4rem 0' }}>
          <div className="container">
            <HeaderContainer>
              <Header>Our Markets</Header>
            </HeaderContainer>
            <Lead>
              Search for homes throughout Ford County, Iroquois County,
              Champaign County and surrounding counties.
            </Lead>
            <MarketList />
          </div>
        </div>
      </div>
    )
  }
}

export default connect(
  null,
  { openContactModal }
)(Home)



// WEBPACK FOOTER //
// ./src/views/Home/index.js